import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
  Autocomplete,
  IconButton,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { FormEvent, useState, useEffect, ChangeEvent } from "react";
import useFarmers from "../../services/useFarmers";
import useCoreProductCategories from "../../services/useCoreProductCategories";
import useBadges from "../../services/useBadges";
import { IProductCore } from "../../types/products";
import { UpdateCoreProductPayload } from "../../services/useUpdateCoreProduct";
import { CreateCoreProductPayload } from "../../services/useCreateCoreProduct";

type CoreProductFormBaseProps = {
  isSubmitting?: boolean;
  errorMessage?: string;
  onCloseErrorMessage?: () => void;
};

type CoreProductFormCreateProps = CoreProductFormBaseProps & {
  isCreateMode: true;
  initialValues?: undefined; // não usamos initialValues na criação
  onSubmit: (values: CreateCoreProductPayload) => void;
};

type CoreProductFormEditProps = CoreProductFormBaseProps & {
  isCreateMode?: false; // ou não definido
  initialValues: IProductCore; // obrigatório na edição
  onSubmit: (values: UpdateCoreProductPayload) => void;
};

type CoreProductFormProps =
  | CoreProductFormCreateProps
  | CoreProductFormEditProps;

const CoreProductForm = (props: CoreProductFormProps) => {
  const {
    isSubmitting = false,
    errorMessage,
    onCloseErrorMessage = () => {},
  } = props;
  const { data: farmers } = useFarmers();
  const { data: categories } = useCoreProductCategories();
  const { data: badges } = useBadges();

  // Estados do formulário
  const [name, setName] = useState("");
  const [producerId, setProducerId] = useState<number | "">("");
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [unit, setUnit] = useState("");
  const [ingredients, setIngredients] = useState("");
  const [selectedBadgeIds, setSelectedBadgeIds] = useState<number[]>([]);
  const [refrigerated, setRefrigerated] = useState(false);
  const [stackOrder, setStackOrder] = useState<number | "">("");
  const [active, setActive] = useState(true);

  // Preencher formulário com valores iniciais (apenas em modo edição)
  useEffect(() => {
    if (!props.isCreateMode && props.initialValues) {
      setName(props.initialValues.name || "");
      setProducerId(props.initialValues.producer?.id || "");
      setCategoryId(props.initialValues.category?.id || "");
      setImageFile(null); // Resetar arquivo ao carregar valores iniciais
      setDescription(props.initialValues.description || "");
      setUnit(props.initialValues.unit || "");
      setIngredients(props.initialValues.ingredients || "");
      setSelectedBadgeIds(
        props.initialValues.badges?.map((badge) => badge.id) || [],
      );
      setRefrigerated(props.initialValues.refrigerated || false);
      setStackOrder(props.initialValues.stack_order || "");
      setActive(
        props.initialValues.active !== undefined
          ? props.initialValues.active
          : true,
      );
    }
  }, [props.isCreateMode, props.initialValues]);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setImageFile(event.target.files[0]);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    // Validação básica
    if (!name.trim()) {
      return;
    }
    if (!categoryId) {
      return;
    }

    // Usar o discriminante para determinar o tipo
    if (props.isCreateMode) {
      // Modo criação: validar producer e montar CreateCoreProductPayload
      if (!producerId) {
        return;
      }

      const createPayload: CreateCoreProductPayload = {
        name: name.trim(),
        category: Number(categoryId),
        producer: Number(producerId),
        description: description.trim() || undefined,
        unit: unit.trim() || undefined,
        ingredients: ingredients.trim() || undefined,
        badges: selectedBadgeIds,
        refrigerated,
        stack_order: stackOrder ? Number(stackOrder) : undefined,
        active,
        imageFile: imageFile || null,
      };

      props.onSubmit(createPayload);
    } else {
      // Modo edição: montar UpdateCoreProductPayload
      const updatePayload: UpdateCoreProductPayload = {
        name: name.trim(),
        category: Number(categoryId),
        description: description.trim() || undefined,
        unit: unit.trim() || undefined,
        ingredients: ingredients.trim() || undefined,
        badges: selectedBadgeIds,
        refrigerated,
        stack_order: stackOrder ? Number(stackOrder) : undefined,
        active,
        imageFile: imageFile || null,
      };

      props.onSubmit(updatePayload);
    }
  };

  // Ordenar categorias por nome
  const sortedCategories = categories
    ? [...categories].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
    : [];

  // Ordenar badges por nome
  const sortedBadges = badges
    ? [...badges].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
    : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
      }}
    >
      {errorMessage ? (
        <Alert onClose={() => onCloseErrorMessage()} severity="error">
          {errorMessage}
        </Alert>
      ) : null}
      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "2rem",
          }}
        >
          <TextField
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            variant="outlined"
            required
            fullWidth
          />

          <FormControl fullWidth required={props.isCreateMode}>
            <InputLabel>Produtor</InputLabel>
            <Select
              value={producerId}
              label="Produtor"
              disabled={!props.isCreateMode}
              onChange={(e) => setProducerId(e.target.value as number | "")}
            >
              {farmers?.map((farmer) => (
                <MenuItem key={farmer.id} value={farmer.id}>
                  {farmer.name}
                </MenuItem>
              ))}
            </Select>
            {!props.isCreateMode && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                O produtor não pode ser alterado após a criação do produto.
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={categoryId}
              label="Categoria"
              onChange={(e) => setCategoryId(e.target.value as number | "")}
            >
              {sortedCategories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Button
              component="label"
              role={undefined}
              variant="outlined"
              tabIndex={-1}
              fullWidth
            >
              {imageFile ? "Alterar imagem" : "Selecionar imagem"}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
            </Button>
            {imageFile && (
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="body2">{imageFile.name}</Typography>
                <IconButton
                  size="small"
                  onClick={() => setImageFile(null)}
                  aria-label="remover imagem"
                >
                  <Delete />
                </IconButton>
              </Box>
            )}
            {!imageFile &&
              !props.isCreateMode &&
              props.initialValues?.image && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  Imagem atual: {props.initialValues.image}
                </Typography>
              )}
          </Box>

          <TextField
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            variant="outlined"
            multiline
            rows={4}
            fullWidth
          />

          <TextField
            label="Unidade"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            variant="outlined"
            fullWidth
            helperText="Ex: kg, un, litro"
          />

          <TextField
            label="Ingredientes"
            value={ingredients}
            onChange={(e) => setIngredients(e.target.value)}
            variant="outlined"
            multiline
            rows={3}
            fullWidth
          />

          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={sortedBadges}
              getOptionLabel={(option) => option.name}
              value={sortedBadges.filter((badge) =>
                selectedBadgeIds.includes(badge.id),
              )}
              onChange={(_, newValue) => {
                setSelectedBadgeIds(newValue.map((badge) => badge.id));
              }}
              renderInput={(params) => <TextField {...params} label="Badges" />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    icon={
                      option.icon ? (
                        <span style={{ fontSize: "16px" }}>{option.icon}</span>
                      ) : undefined
                    }
                    style={{
                      backgroundColor: option.color || undefined,
                      color: option.color ? "#fff" : undefined,
                    }}
                  />
                ))
              }
            />
          </FormControl>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={refrigerated}
                  onChange={(e) => setRefrigerated(e.target.checked)}
                />
              }
              label="Refrigerado"
            />
          </FormGroup>

          <FormControl fullWidth>
            <InputLabel>Ordem de empilhamento</InputLabel>
            <Select
              value={stackOrder}
              label="Ordem de empilhamento"
              onChange={(e) => {
                const value = e.target.value;
                setStackOrder(value === "" ? "" : Number(value));
              }}
            >
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4</MenuItem>
              <MenuItem value={5}>5</MenuItem>
            </Select>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Quanto maior o número, mais pesado o item, e portanto, ele ficará
              mais perto do fundo da caixa.
            </Typography>
          </FormControl>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
              }
              label="Ativo"
            />
          </FormGroup>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default CoreProductForm;
